import { Modal } from "antd";
import { ConfigProvider } from "antd-v5";
import AuthenticatedRoute from "components/AuthenticatedRoute";
import withSuspenseFallback from "components/hoc/withSuspenseFallback";
import { ANTD_V5_THEME } from "constants/AntdV5Theme";
import { ROLE_SUPERADMIN, ROLE_SUPERADMINREAD } from "constants/RBACConstants";
import ErrorBoundary from "containers/ErrorBoundary";
import { useKeyboardNavigation } from "hooks/useKeyboardNavigation";
import TitleManager from "less/components/TitleManager";
import React, { useEffect } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { isInIframe } from "utils/iframeUtils";
import ModalUtil from "utils/modalUtil";
import PageNotFound from "views/PageNotFound/PageNotFound";

const Login = withSuspenseFallback(
  React.lazy(() => import("views/Login/Login")),
);
const Logout = withSuspenseFallback(
  React.lazy(() => import("views/Logout/Logout")),
);
const Register = withSuspenseFallback(
  React.lazy(() => import("views/Register/Register")),
);
const TenantSwitcher = withSuspenseFallback(
  React.lazy(() => import("views/TenantSwitcher/TenantSwitcher")),
);
const CreateTenant = withSuspenseFallback(
  React.lazy(() => import("views/CreateTenant/CreateTenant")),
);
const ACIconGallery = withSuspenseFallback(
  React.lazy(() => import("views/ACIconGallery/ACIconGallery")),
);
const AnyaKitchenSink = withSuspenseFallback(
  React.lazy(() => import("@features/anya-v2-poc/views/AnyaKitchenSink")),
);
const TotpEnroll = withSuspenseFallback(
  React.lazy(() => import("views/Login/TotpEnroll")),
);
const AzureExtensionConfig = withSuspenseFallback(
  React.lazy(() => import("views/AzureExtension/AzureExtensionConfig")),
);
const AzureExtension = withSuspenseFallback(
  React.lazy(() => import("views/AzureExtension/AzureExtension")),
);
const AzureExtensionDetail = withSuspenseFallback(
  React.lazy(() => import("views/AzureExtension/AzureExtensionDetail")),
);
const SuperAdminMain = withSuspenseFallback(
  React.lazy(() => import("containers/SuperAdminMain")),
);
const Main = withSuspenseFallback(
  React.lazy(() => import("containers/Main/Main")),
);
const SeekAccess = withSuspenseFallback(
  React.lazy(() => import("views/SeekAccess/SeekAccess")),
);
const AWSMarketPlaceSignUp = withSuspenseFallback(
  React.lazy(() => import("views/AWSMarketPlaceSignUp/AWSMarketPlaceSignUp")),
);
const ClearLocalStorage = withSuspenseFallback(
  React.lazy(() => import("views/ClearLocalStorage")),
);

export default function App() {
  const history = useHistory();

  // Global keyboard navigation detection for focus indicator styling
  useKeyboardNavigation();

  useEffect(() => {
    const unlisten = history.listen(() => {
      Modal.destroyAll();
      ModalUtil.closeAll();
    });
    return () => unlisten();
  }, [history]);

  const isIframe = isInIframe();
  const isLocalhost = window.location.hostname === "localhost";

  return (
    <ConfigProvider theme={ANTD_V5_THEME} prefixCls="ant-v5">
      <ErrorBoundary>
        <TitleManager />
        <Switch>
          {!isIframe && <Route path="/logout" component={Logout} />}
          {!isIframe && (
            <Route path="/login/totp-enroll" component={TotpEnroll} />
          )}
          {!isIframe && <Route path="/login" component={Login} />}
          {!isIframe && <Route path="/register" component={Register} />}
          <Route path="/404" component={PageNotFound} />
          {isLocalhost && (
            <Route path="/iconGallery" component={ACIconGallery} />
          )}
          {isLocalhost && (
            <Route path="/anyaKitchenSink" component={AnyaKitchenSink} />
          )}
          {!isIframe && <Route path="/seekAccess" component={SeekAccess} />}
          {!isIframe && (
            <Route path="/mktplaceSignUp" component={AWSMarketPlaceSignUp} />
          )}
          {!isIframe && (
            <Route path="/clear-local" component={ClearLocalStorage} />
          )}
          {isIframe && (
            <Route
              path="/azure-extension/findings"
              component={AzureExtension}
            />
          )}
          {isIframe && (
            <Route
              path="/azure-extension/config"
              component={AzureExtensionConfig}
            />
          )}
          {isIframe && (
            <Route
              path="/azure-extension/findingDetail/:findingId"
              component={AzureExtensionDetail}
            />
          )}

          {/* if iframe then route all paths to azure, to avoid accidental display of internal pages */}
          {isIframe && <Route path="/" component={AzureExtensionConfig} />}
          {!isIframe && (
            <AuthenticatedRoute
              key="superAdmin"
              path="/superAdmin"
              component={SuperAdminMain}
              accessRoles={[ROLE_SUPERADMIN, ROLE_SUPERADMINREAD]}
            />
          )}
          {!isIframe && (
            <AuthenticatedRoute
              key="tenantSwitch"
              path="/tenantSwitch"
              component={TenantSwitcher}
            />
          )}
          {!isIframe && (
            <AuthenticatedRoute
              key="createTenant"
              path="/createTenant"
              component={CreateTenant}
            />
          )}
          {!isIframe && (
            <AuthenticatedRoute key="app" path="/" component={Main} />
          )}
        </Switch>
      </ErrorBoundary>
    </ConfigProvider>
  );
}
